import React, {useEffect, useState} from "react";
import ShippingForm, {IShippingFormData} from "./ShippingForm";
import {useTranslation} from "react-i18next";
import {captureException} from "@sentry/browser";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import Spinner from "../../../utils/loader/Spinner";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {ICheckoutPutParams} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import {convertToAddressData, isSameAddress} from "../AddressStep/AddressStep";
import {CheckoutError, DEFAULT_ERROR_MESSAGE, prepareTrackingProductData} from "../../Checkout";
import window from "../../../lib/WindowExtended";
import {useCheckoutViewContext} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import {ApiError} from "@connox/typescript-library";
import {PossibleNetworkError} from "../../../cart/ShoppingCart/ShoppingCart";
import {INTERNAL_ERROR_TYPE} from "../../../lib/payments/PaymentController";
import genericStyles from "../../../lib/GenericStyles.module.scss";
import {ICartMessage} from "@connox/typescript-library/types/cart/CartMessage";
import {Tracking} from "../../../../tracking/tracking";

const Shipping: React.FC<{isVisible: boolean}> = ({isVisible}) => {
  const {i18n, t} = useTranslation(["errorcodes", "translations"]);
  const [phoneError, setPhoneError] = useState<string>("");
  const {gotoStep, shippingOptions} = useCheckoutViewContext();
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shippingData, setShippingData] = useState<IShippingFormData>();
  const {checkout} = useCheckoutContext();
  const {deliveryAddress, invoiceAddress} = checkout;

  useEffect(() => {
    if (!isVisible) {
      setIsLoading(false);
      setErrors([]);
      setPhoneError("");
      window.isIntoSettingPhoneNoInShippingStep = false;
    }
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const submitData = async () => {
    const {shippingOption, phone, shippingDate, isPartDeliveryAllowed} = shippingData as IShippingFormData;
    const {isPhoneRequired, logisticsTypeId} = shippingOption;

    if (isPhoneRequired && !phone) {
      setPhoneError(t("form.pleaseComplete"));

      return;
    }

    setIsLoading(true);

    let shippingParams: ICheckoutPutParams = {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      favouredDateShipping: shippingDate,
      isPartDeliveryAllowed,
      logisticsTypeId,
    };

    if (deliveryAddress && phone && isPhoneRequired) {
      const deliveryConverted = convertToAddressData(deliveryAddress);
      if (isSameAddress(deliveryAddress, invoiceAddress)) {
        shippingParams = {
          ...shippingParams,
          address: {
            delivery: {...deliveryConverted, phone},
            invoice: {...deliveryConverted, phone},
          },
        };
      } else if (invoiceAddress) {
        const invoiceConverted = convertToAddressData(invoiceAddress);
        shippingParams = {
          ...shippingParams,
          address: {
            delivery: {...deliveryConverted, phone},
            invoice: invoiceConverted
          },
        };
      } else {
        setErrors([t("checkout.emptyInvoiceAddress")]);

        return;
      }
    }
    if (phone !== deliveryAddress?.phone) {
      window.isIntoSettingPhoneNoInShippingStep = true;
    }
    if (
      (shippingParams.favouredDateShipping ?? null) === checkout.favouredDateShipping &&
      shippingParams.isPartDeliveryAllowed === checkout.isPartDeliveryAllowed &&
      (shippingParams.logisticsTypeId ?? null) === checkout.logisticsTypeId &&
      phone === deliveryAddress?.phone
    ) {
      gotoStep();

      return;
    }

    const shippingTier = shippingOption.name || "";
    const productData = prepareTrackingProductData(checkout.cartData);
    Tracking.sendAddShippingInfoEvent(shippingTier, productData);

    try {
      await checkout.put(shippingParams);
      setErrors([]);
      setIsLoading(false);
    } catch (err) {
      setErrors([]);
      setPhoneError("");
      setIsLoading(false);
      const tempError = ((err as ApiError).originalError || err) as PossibleNetworkError;
      const result = tempError.response?.data.result?.code || tempError.response?.data.result;
      const tempMessage: string = result || tempError.message;
      const tempMessages: ICartMessage[] | undefined = tempError.response?.data.messages;
      let errMessages: string[];
      if (tempMessages) {
        errMessages = tempMessages.map((msg) => msg.code);
      } else {
        errMessages = [tempMessage];
      }

      let sentryEventId: string | undefined;
      if (errMessages.some((msg) => !i18n.exists(msg))) {
        tempError.name = INTERNAL_ERROR_TYPE.SHIPPING.OnCatch + tempError.name;
        sentryEventId = captureException(tempError, {extra: {place: "Shipping after checkout put", errMessages}});
      }

      const singlePhoneError = errMessages.find((msg) => msg.toLowerCase().includes("phone"));
      if (singlePhoneError) {
        setPhoneError(singlePhoneError);
      }

      setErrors(
        errMessages
          .filter((msg) => msg !== singlePhoneError)
          .map((errMsg) => t(errMsg, t(DEFAULT_ERROR_MESSAGE, {sentryEventId}))),
      );
    }
  };

  return (
    <div className={`Shipping ${genericStyles.checkoutContainer} ${genericStyles.mobileOnlyFriendly}`}>
      <h1>{t("checkout.shippingOptions")}</h1>
      <CheckoutError>{errors}</CheckoutError>
      <ShippingForm
        phoneError={phoneError}
        onChange={(data) => {
          setShippingData(data);
        }}
      />
      <CartDetails
        selectedShippingOption={shippingOptions.find(
          (option) => option.logisticsTypeId === shippingData?.shippingOption.logisticsTypeId,
        )}
      />
      <Button
        action={(evt) => {
          evt.preventDefault();
          void submitData();
        }}
        styles={[ButtonStyles.wide]}
        type={isLoading ? ButtonType.disabled : ButtonType.default}
      >
        {isLoading ? <Spinner /> : <span>{t("general.continue")}</span>}
      </Button>
    </div>
  );
};

export default Shipping;
